// Settlement geometry + palettes — the instanced forms (houses, keeps,
// fortifications, dominion-path centerpieces) and their weathered palettes.
// Split out of Settlements.tsx; the layout builders live in settlementBuild.ts,
// the React pieces in SettlementPieces.tsx.

import * as THREE from 'three';
import { bakeShade, merge } from './TerrainScatterMaterials.js';
import type { CultureForm } from './settlementCultures.js';

/** A gable roof: triangular prism, ridge along X, eaves overhanging the walls. */
export function gableRoof(width: number, depth: number, height: number): THREE.BufferGeometry {
  const shape = new THREE.Shape();
  shape.moveTo(-depth / 2, 0);
  shape.lineTo(depth / 2, 0);
  shape.lineTo(0, height);
  shape.closePath();
  return new THREE.ExtrudeGeometry(shape, { depth: width, bevelEnabled: false })
    .rotateY(Math.PI / 2)
    .translate(-width / 2, 0, 0);
}

const WALL_SHADE = { bottom: 0.72, top: 1.02, jitter: 0.1 };
const ROOF_SHADE = { bottom: 0.78, top: 1.08, jitter: 0.16 };

/** Walls + roof for a house footprint; roof sits on the wall top. */
function house(w: number, d: number, h: number, roofH: number, seed: number) {
  return {
    walls: bakeShade(new THREE.BoxGeometry(w, h, d).translate(0, h / 2, 0), {
      ...WALL_SHADE,
      seed,
    }),
    roof: bakeShade(gableRoof(w * 1.12, d * 1.18, roofH).translate(0, h, 0), {
      ...ROOF_SHADE,
      seed: seed + 1,
    }),
  };
}

const cottage = house(0.26, 0.2, 0.15, 0.12, 101);
const longhouse = house(0.38, 0.2, 0.15, 0.12, 111);
const townhouse = house(0.21, 0.2, 0.28, 0.13, 121);

// Keep: a round stone tower with a conical roof.
const keepWalls = bakeShade(new THREE.CylinderGeometry(0.14, 0.17, 0.5, 8).translate(0, 0.25, 0), {
  ...WALL_SHADE,
  seed: 131,
});
const keepRoof = bakeShade(new THREE.ConeGeometry(0.2, 0.27, 8).translate(0, 0.635, 0), {
  ...ROOF_SHADE,
  seed: 132,
});

// Ruin: uneven broken walls, no roof, a rubble heap (authored at house scale / 1.5).
const scaled = (g: THREE.BufferGeometry) => g.scale(1.5, 1.5, 1.5);
const ruinWalls = scaled(
  merge([
    bakeShade(new THREE.BoxGeometry(0.17, 0.07, 0.025).translate(0, 0.035, -0.055), {
      ...WALL_SHADE,
      seed: 141,
    }),
    bakeShade(new THREE.BoxGeometry(0.025, 0.045, 0.13).translate(-0.075, 0.0225, 0), {
      ...WALL_SHADE,
      seed: 142,
    }),
    bakeShade(new THREE.BoxGeometry(0.08, 0.03, 0.025).translate(0.045, 0.015, 0.055), {
      ...WALL_SHADE,
      seed: 143,
    }),
  ]),
);
const ruinRubble = scaled(
  merge([
    bakeShade(new THREE.IcosahedronGeometry(0.035, 0).translate(0.03, 0.02, 0.0), {
      bottom: 0.7,
      top: 1,
      jitter: 0.2,
      seed: 144,
    }),
    bakeShade(new THREE.IcosahedronGeometry(0.025, 0).translate(0.07, 0.012, -0.02), {
      bottom: 0.7,
      top: 1,
      jitter: 0.2,
      seed: 145,
    }),
  ]),
);

// --- Fortifications (walls_1..3, tower_1..3) ---
// Walls run along the city hex's edges at WALL_RADIUS, built from segments a
// third of an edge long so nothing is stretched; the gate is a missing
// middle third. Authored along X at world size (no settlement scale).

export const WALL_RADIUS = 0.93; // hex corner radius the wall line follows
const SEGMENT = WALL_RADIUS / 3; // regular hexagon: edge length == radius

// walls_1 — timber palisade: a row of logs with pointed tips.
const palisadeLogs = merge(
  Array.from({ length: 5 }, (_, i) =>
    bakeShade(
      new THREE.CylinderGeometry(0.024, 0.028, 0.15, 5).translate(
        -SEGMENT / 2 + SEGMENT * ((i + 0.5) / 5),
        0.075,
        0,
      ),
      { bottom: 0.7, top: 1, jitter: 0.2, seed: 201 + i },
    ),
  ),
);
const palisadeTips = merge(
  Array.from({ length: 5 }, (_, i) =>
    bakeShade(
      new THREE.ConeGeometry(0.026, 0.05, 5).translate(
        -SEGMENT / 2 + SEGMENT * ((i + 0.5) / 5),
        0.175,
        0,
      ),
      { bottom: 0.75, top: 1, jitter: 0.2, seed: 211 + i },
    ),
  ),
);

// walls_2 — stone curtain wall with a coping course.
const stoneWall = bakeShade(
  new THREE.BoxGeometry(SEGMENT + 0.01, 0.16, 0.06).translate(0, 0.08, 0),
  {
    bottom: 0.7,
    top: 1,
    jitter: 0.14,
    seed: 221,
  },
);
const stoneCoping = bakeShade(
  new THREE.BoxGeometry(SEGMENT + 0.02, 0.025, 0.08).translate(0, 0.172, 0),
  {
    bottom: 0.85,
    top: 1.05,
    jitter: 0.1,
    seed: 222,
  },
);

// walls_3 — high wall with battlements.
const highWall = bakeShade(
  new THREE.BoxGeometry(SEGMENT + 0.01, 0.24, 0.07).translate(0, 0.12, 0),
  {
    bottom: 0.68,
    top: 1,
    jitter: 0.14,
    seed: 231,
  },
);
const merlons = merge(
  Array.from({ length: 3 }, (_, i) =>
    bakeShade(
      new THREE.BoxGeometry(0.05, 0.045, 0.075).translate(
        -SEGMENT / 2 + SEGMENT * ((i + 0.5) / 3),
        0.262,
        0,
      ),
      { bottom: 0.85, top: 1.05, jitter: 0.12, seed: 232 + i },
    ),
  ),
);

// tower_1..3 — round corner towers with conical roofs (height grows by level).
const towerBody = bakeShade(
  new THREE.CylinderGeometry(0.075, 0.09, 0.32, 8).translate(0, 0.16, 0),
  {
    ...WALL_SHADE,
    seed: 241,
  },
);
const towerRoof = bakeShade(new THREE.ConeGeometry(0.11, 0.16, 8).translate(0, 0.4, 0), {
  ...ROOF_SHADE,
  seed: 242,
});

export const PALISADE_WOOD = ['#6b4f35', '#76583b', '#5f4630'];
export const PALISADE_TIP = ['#5a4230', '#4e3a2a'];

// --- Dominion-path centerpieces (dominion-path visuals, M2) ---
// One silhouette prop per doctrine archetype (spec §2.1): the city reads as
// Industry/Science/… from its centerpiece before any tooltip. Same
// instancing contract as every other form — geometry below, palette rows in
// PATH_WALLS/PATH_ROOFS, placement in buildPathProps.

// Industry — a riveted smokestack: stacked cylinder with a wider cap.
const stackBase = bakeShade(
  new THREE.CylinderGeometry(0.05, 0.062, 0.22, 8).translate(0, 0.11, 0),
  { bottom: 0.66, top: 1, jitter: 0.1, seed: 301 },
);
const stackCap = bakeShade(
  new THREE.CylinderGeometry(0.066, 0.056, 0.05, 8).translate(0, 0.245, 0),
  {
    bottom: 0.7,
    top: 1.02,
    jitter: 0.1,
    seed: 302,
  },
);

// Science — a thin mast with a glass orb.
const mastPole = bakeShade(new THREE.CylinderGeometry(0.014, 0.02, 0.3, 5).translate(0, 0.15, 0), {
  bottom: 0.72,
  top: 1,
  jitter: 0.08,
  seed: 311,
});
const mastOrb = bakeShade(new THREE.IcosahedronGeometry(0.05, 0).translate(0, 0.33, 0), {
  bottom: 0.85,
  top: 1.05,
  jitter: 0.06,
  seed: 312,
});

// Faith — a slender bell spire with a gold finial.
const spireBody = bakeShade(new THREE.CylinderGeometry(0.045, 0.06, 0.2, 6).translate(0, 0.1, 0), {
  ...WALL_SHADE,
  seed: 321,
});
const spireTip = bakeShade(new THREE.ConeGeometry(0.055, 0.18, 6).translate(0, 0.29, 0), {
  ...ROOF_SHADE,
  seed: 322,
});
const spireFinial = bakeShade(new THREE.SphereGeometry(0.024, 6, 5).translate(0, 0.395, 0), {
  bottom: 0.9,
  top: 1.05,
  jitter: 0.05,
  seed: 323,
});

// Arcane — a tilted rune monolith (the tilt comes from rotZ at placement)
// crowned by a small floating glyph shard.
const monolith = bakeShade(new THREE.BoxGeometry(0.07, 0.3, 0.045).translate(0, 0.15, 0), {
  bottom: 0.62,
  top: 1.08,
  jitter: 0.12,
  seed: 331,
});
const glyphShard = bakeShade(new THREE.TetrahedronGeometry(0.038).translate(0, 0.4, 0), {
  bottom: 0.9,
  top: 1.1,
  jitter: 0.1,
  seed: 332,
});

// Nature — a living grove: trunk + oversized blossom canopy.
const groveTrunk = bakeShade(
  new THREE.CylinderGeometry(0.025, 0.035, 0.14, 5).translate(0, 0.07, 0),
  { bottom: 0.7, top: 1, jitter: 0.14, seed: 341 },
);
const groveCanopy = bakeShade(new THREE.IcosahedronGeometry(0.11, 0).translate(0, 0.21, 0), {
  bottom: 0.8,
  top: 1.06,
  jitter: 0.18,
  seed: 342,
});

// Chaos — a warped obelisk: tapered prism with a jagged tip.
const obeliskBody = bakeShade(
  new THREE.CylinderGeometry(0.03, 0.058, 0.26, 4).translate(0, 0.13, 0),
  { bottom: 0.6, top: 1.05, jitter: 0.16, seed: 351 },
);
const obeliskTip = bakeShade(new THREE.ConeGeometry(0.034, 0.09, 4).translate(0, 0.305, 0), {
  bottom: 0.65,
  top: 1.1,
  jitter: 0.2,
  seed: 352,
});

export type BuildingForm =
  | CultureForm
  | 'cottage'
  | 'longhouse'
  | 'townhouse'
  | 'keep'
  | 'ruin'
  | 'palisade'
  | 'wall'
  | 'highWall'
  | 'tower'
  | 'pathIndustry'
  | 'pathScience'
  | 'pathFaith'
  | 'pathArcane'
  | 'pathNature'
  | 'pathChaos';
export type BuildingStyle = 'timber' | 'stone' | 'brick';
export type Part = 'walls' | 'roof';

export const FORM_PARTS: Record<
  Exclude<BuildingForm, CultureForm>,
  Record<Part, THREE.BufferGeometry>
> = {
  cottage: cottage,
  longhouse: longhouse,
  townhouse: townhouse,
  keep: { walls: keepWalls, roof: keepRoof },
  // A ruin's "roof" slot is its rubble heap — same instancing path.
  ruin: { walls: ruinWalls, roof: ruinRubble },
  palisade: { walls: palisadeLogs, roof: palisadeTips },
  wall: { walls: stoneWall, roof: stoneCoping },
  highWall: { walls: highWall, roof: merlons },
  tower: { walls: towerBody, roof: towerRoof },
  // Path centerpieces: "walls" is the body, "roof" the crowning element.
  pathIndustry: { walls: stackBase, roof: stackCap },
  pathScience: { walls: mastPole, roof: mastOrb },
  pathFaith: { walls: spireBody, roof: merge([spireTip, spireFinial]) },
  pathArcane: { walls: monolith, roof: glyphShard },
  pathNature: { walls: groveTrunk, roof: groveCanopy },
  pathChaos: { walls: obeliskBody, roof: obeliskTip },
};

export const material = new THREE.MeshBasicMaterial({
  color: 'rgb(255,255,255)',
  vertexColors: true,
});

// --- Palettes (sRGB hex; muted, weathered) ---

export const WALL_PALETTES: Record<BuildingStyle, readonly string[]> = {
  timber: ['#d9c9a3', '#cdb98f', '#e2d4b2', '#bfa67c'], // plaster / limewash
  stone: ['#8d8a82', '#9d998f', '#7f7b73', '#a8a397'],
  brick: ['#9a5a43', '#a8644a', '#8a4f3c', '#b0714f'],
};
export const ROOF_PALETTES: Record<BuildingStyle, readonly string[]> = {
  timber: ['#a88a4f', '#b39458', '#98794a', '#8c7246'], // thatch
  stone: ['#4f565e', '#5b636b', '#454c54', '#626970'], // slate
  brick: ['#9b4a32', '#a9553a', '#8a432f', '#b3603f'], // terracotta
};
export const RUIN_WALL = ['#6f6a62', '#7a746a', '#655f57'];
export const RUBBLE = ['#5d5850', '#6b655b'];

// Dominion-path centerpiece palettes (spec §2.1 accents, weathered to sit in
// the settlement's muted register; Dark variants read near-black with a
// sparse warm/blood accent — never grey mush).
export type Archetype = 'industry' | 'science' | 'faith' | 'arcane' | 'nature' | 'chaos';

export const PATH_PALETTES: Record<
  Archetype,
  { readonly walls: readonly string[]; readonly roofs: readonly string[] }
> = {
  industry: {
    walls: ['#6e5f4e', '#7a6a55', '#5f5244'], // riveted iron, rusted
    roofs: ['#8a5a33', '#96703f', '#7a4d2b'], // rusted copper
  },
  science: {
    walls: ['#5e6a70', '#6a7780', '#525c62'], // pale gunmetal
    roofs: ['#79c9cf', '#8ad4d8', '#68b5bd'], // luminous glass
  },
  faith: {
    walls: ['#b5a688', '#c0b193', '#a3947a'], // ivory stone
    roofs: ['#c9a540', '#d4b04c', '#b8973a'], // gold
  },
  arcane: {
    walls: ['#4f465e', '#5a5068', '#443c52'], // runestone
    roofs: ['#a486d8', '#b294e2', '#9478c8'], // soft emissive violet
  },
  nature: {
    walls: ['#5f5340', '#6a5c47', '#544a3a'], // living wood
    roofs: ['#6fae4e', '#7fbc5c', '#5f9c42'], // leaf and blossom
  },
  chaos: {
    walls: ['#3a3238', '#453c42', '#2f282e'], // warped black stone
    roofs: ['#a1375f', '#ad4168', '#8f2d54'], // blood magenta
  },
};

const _c = new THREE.Color();
const _hsl = { h: 0, s: 0, l: 0 };

/** Dark-alignment tone: pull a palette entry toward cooled near-black. */
function darkTone(hex: string): string {
  _c.set(hex);
  _c.getHSL(_hsl);
  _c.setHSL(_hsl.h, _hsl.s * 0.55, _hsl.l * 0.42);
  return `#${_c.getHexString()}`;
}

export function pathPalette(
  archetype: Archetype,
  alignment: string | undefined,
): { readonly walls: readonly string[]; readonly roofs: readonly string[] } {
  const base = PATH_PALETTES[archetype];
  if (alignment !== 'dark') return base;
  return {
    walls: base.walls.map(darkTone),
    roofs: base.roofs.map(darkTone),
  };
}
